import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  InstagramConnectionStatusView,
  InstagramConnectionSummary,
  InstagramPlatformCredential,
  UpdateInstagramSettingsPayload,
  UpsertInstagramPlatformCredentialPayload,
} from '../types';

/**
 * osprey-api Instagram routes (PE-2178 FE, backend PE-2177). Three tiers, three gates:
 *
 *   • `me/instagram/*` — MERCHANT self-service. No permission guard; watchman resolves the
 *     merchant from the JWT (`user.merchantId ?? user.tenantId`). A COLLECTIVE session has
 *     NEITHER, so these routes are unusable for TCS staff — hence the merchant-scoped page
 *     and the separate admin surfaces below. `POST me/instagram/connect` is the exception:
 *     osprey-api reads `x-tenant-id` off the request to mint the signed OAuth state, and
 *     ApiFetch already sends that header from the auth store.
 *   • `instagram/connections` — TCS admin oversight. MERCHANT.READ (+ reset on
 *     MERCHANT.UPDATE). Both are granted today.
 *   • `instagram/platform-credential` — TCS admin. MERCHANT.MANAGE.
 *
 * ⚠️ MERCHANT.MANAGE IS GRANTED TO NOBODY. watchman's seed gives COLLECTIVE_SUPERADMIN
 * MERCHANT:[CREATE, READ, UPDATE, DEACTIVATE, DELETE] and PermissionGuard is a literal
 * `chars.includes('M')` test with no superadmin bypass, so BOTH platform-credential routes
 * 403 for every existing role. And watchman's `buildAuthorizeUrl` throws "Instagram platform
 * app credential is not configured" until a credential exists — so merchant connect is
 * blocked behind the same grant. The credential page surfaces the 403 as an explicit
 * permission-denied state rather than a generic error; the grant is filed as a backend ask.
 *
 * None of these paths are in ApiFetch's MERCHANT_SCOPED_PREFIXES/TENANT_SCOPED_PREFIXES, and
 * they must not be: they take neither query param and an unexpected one is a 422.
 */
export const instagramApi = {
  // ─── Merchant self-service ────────────────────────────────────────────────

  getMyStatus: async (): Promise<InstagramConnectionStatusView> => {
    const response = await ApiFetch.get<Enveloped<InstagramConnectionStatusView>>(
      '/me/instagram/status',
    );
    return unwrap(response);
  },

  /** Returns the Instagram OAuth authorize URL for the browser to be redirected to. */
  connect: async (): Promise<{ authorizeUrl: string }> => {
    const response = await ApiFetch.post<Enveloped<{ authorizeUrl: string }>>(
      '/me/instagram/connect',
    );
    return unwrap(response);
  },

  disconnect: async (): Promise<InstagramConnectionStatusView> => {
    const response = await ApiFetch.del<Enveloped<InstagramConnectionStatusView>>('/me/instagram');
    return unwrap(response);
  },

  /** PATCH — `ApiFetch.put` issues PATCH, which is what osprey-api declares here. */
  updateMySettings: async (
    payload: UpdateInstagramSettingsPayload,
  ): Promise<InstagramConnectionStatusView> => {
    const response = await ApiFetch.put<Enveloped<InstagramConnectionStatusView>>(
      '/me/instagram/settings',
      payload,
    );
    return unwrap(response);
  },

  // ─── TCS admin oversight ──────────────────────────────────────────────────

  listConnections: async (): Promise<InstagramConnectionSummary[]> => {
    const response = await ApiFetch.get<Enveloped<InstagramConnectionSummary[]>>(
      '/instagram/connections',
    );
    return unwrap(response);
  },

  /** Support action for a connection stuck in ERROR/EXPIRED. MERCHANT.UPDATE. */
  resetConnection: async (merchantId: string): Promise<InstagramConnectionStatusView> => {
    const response = await ApiFetch.post<Enveloped<InstagramConnectionStatusView>>(
      `/instagram/connections/${merchantId}/reset`,
    );
    return unwrap(response);
  },

  // ─── TCS admin platform credential ────────────────────────────────────────

  /** Null when the shared Meta app has never been configured. */
  getPlatformCredential: async (): Promise<InstagramPlatformCredential | null> => {
    const response = await ApiFetch.get<Enveloped<InstagramPlatformCredential | null>>(
      '/instagram/platform-credential',
    );
    return unwrap(response);
  },

  /**
   * ⭐ A REAL PUT. `ApiFetch.put` issues PATCH — `legacy_put` is the one that issues PUT, and
   * osprey-api declares this route as `@Put('platform-credential')`.
   */
  upsertPlatformCredential: async (
    payload: UpsertInstagramPlatformCredentialPayload,
  ): Promise<InstagramPlatformCredential> => {
    const response = await ApiFetch.legacy_put<Enveloped<InstagramPlatformCredential>>(
      '/instagram/platform-credential',
      payload,
    );
    return unwrap(response);
  },
};
